// src/admin/components/task/TaskReportModal.jsx
import React, { useEffect, useState } from 'react';
import { devError } from '../../../shared/utils/clientLog';
import AdminModal from '../common/AdminModal';
import { fetchWithAuth } from '../../auth/fetchWithAuth';
import useTrackPageVisit from '../../hooks/useTrackPageVisit';
import TaskTimelineSection from './TaskTimelineSection';
import FieldEngineerSection from './FieldEngineerSection';
import ReportSection from './ReportSection';
import TaskCaptureMediaSection from '../captures/TaskCaptureMediaSection';
import TaskPathSection from './TaskPathSection';
import { API_URL } from '../../../config/env';

export default function TaskReportModal({ show, onClose, taskId }) {
  const [taskData, setTaskData] = useState(null);
  const [loading, setLoading] = useState(true);

  useTrackPageVisit('work_record', show);

  useEffect(() => {
    if (show && taskId) {
      setLoading(true);
      fetchWithAuth(`${API_URL}/v1/admin/tasks/${taskId}`)
        .then(res => res.json())
        .then(data => {
          setTaskData(data);
          setLoading(false);
        })
        .catch((error) => {
          console.error('TaskReportModal - error fetching task:', error);
          setLoading(false);
        });
    }
  }, [show, taskId]);

  // Parse work reports safely
  const parseWorkReports = (workReports) => {
    if (!workReports) return {};
    
    try {
      // If it's already an object, return it
      if (typeof workReports === 'object') {
        return workReports;
      }
      
      // If it's a string, try to parse it
      if (typeof workReports === 'string') {
        return JSON.parse(workReports);
      }
      
      return {};
    } catch (error) {
      devError('TaskReportModal - error parsing work_reports:', error);
      return {};
    }
  };

  if (!show) return null;

  const workReports = parseWorkReports(taskData?.work_reports);
  const workReportMeta = workReports._meta;
  const stepReportEntries = Object.entries(workReports).filter(([stepId]) => stepId !== '_meta');

  return (
    <AdminModal
      show={show}
      onClose={onClose}
      title="Task Report"
    >
      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border" role="status" />
        </div>
      ) : (
        <>
          <TaskTimelineSection task={taskData} />
          <FieldEngineerSection engineer={taskData.completed_by_field_engineer} />
          <TaskCaptureMediaSection taskId={taskId} />
          <TaskPathSection meta={workReportMeta} />
          {stepReportEntries.map(([stepId, forms]) =>
            Object.entries(forms)
              .filter(([formSlug]) => formSlug !== '_photo_capture')
              .map(([formSlug, fields]) => (
              <ReportSection 
                key={`${stepId}_${formSlug}`} 
                stepKey={stepId} 
                formId={formSlug} 
                fields={fields} 
              />
            ))
          )}
        </>
      )}
    </AdminModal>
  );
}
